import React from 'react';
import { useQuery } from '@common/hooks';
import {
  LoadingWrapper,
  ZonedTime,
  Divider,
  Text,
  Gutter,
  Gap,
  Col,
  Row,
} from '@common/components';
import { EffectiveBillingCardQuery } from 'works/legacy/graphql/effective-billing-card';
import { toISODateFormat } from '@common/services/datetime';
import { getRateByEmploymentType } from '@institutions/services';
import { isNumber } from 'works/legacy/lib/number-helper';
import { ExtensionDetailItem } from 'works/legacy/components/extension-offer/extension-detail-item';
import {
  ExtensionOfferAssignmentType,
  ExtensionOfferOrganizationUnitType,
} from '@working-experience/graphql/queries/extension-offer-query';
import { BillingCardEmploymentTypeRatesType } from '@institutions/graphql/fragments/billing-card';
import { ShiftType, StaffingRequestType } from '@common/graphql';

export function ExtensionOfferAssignmentSummary({
  assignment,
  billingCard,
  shiftType,
  startDate,
  endDate,
  state,
  billingRegularRate,
  originalBillingRegularRate,
  organizationUnit,
}: {
  assignment?: ExtensionOfferAssignmentType;
  billingCard?: BillingCardEmploymentTypeRatesType | null;
  shiftType: ShiftType;
  startDate: string;
  endDate: string;
  state: string;
  billingRegularRate: number;
  originalBillingRegularRate: number;
  organizationUnit?: ExtensionOfferOrganizationUnitType;
}) {
  const { staffingRequest } = assignment?.staffingRequestApplication || {};

  const isDeviated = !billingCard;

  const { data: effectiveBillingCardData, loading } = useQuery(
    EffectiveBillingCardQuery,
    {
      skip: !isDeviated,
      variables: {
        effectiveDate: toISODateFormat(startDate),
        organizationUnitId: organizationUnit?.id || '',
        roleId: staffingRequest?.role?.id || '',
        roleLevel: staffingRequest?.roleLevel || '',
      },
    }
  );

  const billingCardRate = getRateByEmploymentType(
    billingCard || effectiveBillingCardData?.effectiveBillingCard,
    shiftType,
    assignment?.employmentType
  );

  return (
    <LoadingWrapper loading={loading}>
      <div className="extension-offer-assignment-summary">
        <Divider />
        <Gutter direction="vertical" size="md">
          <Gap vertical>
            <div>
              <Text bold>EXTENSION OFFER DETAILS</Text>
            </div>
            <div className="sentry-mask">
              <Text>
                We are delighted to offer an extension to{' '}
                <Text bold>{assignment?.worksProfile?.fullName || ''}</Text>{' '}
                with a new end date of{' '}
                <Text bold>
                  <ZonedTime date={endDate} variant="short_format" />
                </Text>
                .
              </Text>
            </div>
          </Gap>
        </Gutter>
        <Divider gutter={4} />
        <Row>
          <Col span={6}>
            <ExtensionDetailItem title="ID">
              <ExtensionDetailItem.Value value={staffingRequest?.code || ''} />
            </ExtensionDetailItem>
          </Col>
          <Col span={8}>
            <ExtensionDetailItem title="Role - Assignment">
              <ExtensionDetailItem.Value
                value={getRoleAssignment(
                  staffingRequest?.role?.abbreviation || '',
                  staffingRequest?.staffingRequestType
                )}
              />
            </ExtensionDetailItem>
          </Col>
        </Row>
        <Divider gutter={4} />
        <Row>
          <Col span={6}>
            <ExtensionDetailItem title="Extension Rate">
              <ExtensionDetailItem.Rate
                state={state}
                value={billingRegularRate}
              />
            </ExtensionDetailItem>
          </Col>
          <Col span={6}>
            {isNumber(billingCardRate) ? (
              <ExtensionDetailItem title="Rate Card Rate">
                <ExtensionDetailItem.Rate
                  value={billingCardRate}
                  overrided={isDeviated}
                />
              </ExtensionDetailItem>
            ) : (
              <ExtensionDetailItem title="Rate Card Rate">
                None
              </ExtensionDetailItem>
            )}
          </Col>
          <Col span={6}>
            <ExtensionDetailItem title="Most Recent Rate">
              <ExtensionDetailItem.Rate
                value={originalBillingRegularRate}
                overrided={billingRegularRate !== originalBillingRegularRate}
              />
            </ExtensionDetailItem>
          </Col>
        </Row>
        <Divider gutter={4} />
        <Row>
          <Col span={24}>
            <ExtensionDetailItem title="Facility - City, State">
              <ExtensionDetailItem.Value
                value={getFacilityCityState({
                  organizationUnit,
                  organization: assignment?.organization,
                })}
              />
            </ExtensionDetailItem>
          </Col>
        </Row>
        <Divider gutter={4} />
        <Row>
          <Col span={24}>
            <ExtensionDetailItem title="Unit - Facility">
              <ExtensionDetailItem.Value
                value={getUnitFacility(
                  organizationUnit?.department?.name || '',
                  organizationUnit?.department?.organizationBusiness?.name || ''
                )}
              />
            </ExtensionDetailItem>
          </Col>
        </Row>
        <Divider gutter={4} />
      </div>
    </LoadingWrapper>
  );
}

function getFacilityCityState({
  organizationUnit,
  organization,
}: {
  organizationUnit?: ExtensionOfferOrganizationUnitType;
  organization?: { name: string };
}) {
  const { city, stateCode } =
    organizationUnit?.department?.organizationBusiness || {};

  return `${organization?.name || ''} - ${city}, ${stateCode}`;
}

function getUnitFacility(
  departmentName: string,
  organizationBusinessName: string
) {
  return `${departmentName} - ${organizationBusinessName}`;
}

function getRoleAssignment(
  staffingRequestRoleAbbreviation?: string,
  staffingRequestType?: StaffingRequestType
) {
  if (!staffingRequestRoleAbbreviation || !staffingRequestType) {
    return '';
  }

  const humanizedStaffingRequestType =
    staffingRequestType === 'long_term' ? 'Long Term' : 'On Demand';

  return `${staffingRequestRoleAbbreviation} - ${humanizedStaffingRequestType}`;
}
